// Buttons (02-building-blocks.md, "Buttons", "Tooltip"): filled for the main action, soft for the
// rest, text buttons in dialogs; icon-only buttons named by their label, shown as a tooltip.
import { useRef, useState, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Icon } from './icons';
import { Spinner } from './Wait';
import { t } from '../i18n';

type Variant = 'filled' | 'soft' | 'text' | 'critical' | 'critical-text' | 'critical-soft';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  small?: boolean;
  /** Shows a small spinner before the text and disables the button. */
  busy?: boolean;
  ref?: React.Ref<HTMLButtonElement>;
}

export function Button({ variant = 'soft', small, busy, className, children, disabled, ...rest }: ButtonProps) {
  return (
    <button
      type="button"
      className={`btn btn-${variant}${small ? ' btn-small' : ''}${className ? ` ${className}` : ''}`}
      disabled={disabled || busy}
      {...rest}
    >
      {busy && <Spinner size={16} />}
      {children}
    </button>
  );
}

const TOOLTIP_DELAY = 400;

/** A tooltip beside or below its trigger (or opposite, to fit), on a mouse's hover or keyboard focus. */
export function useTooltip(label: ReactNode, side: 'below' | 'right' = 'below') {
  const tip = useRef<HTMLDivElement>(null);
  const timer = useRef<number>(undefined);
  const show = (target: HTMLElement) => {
    timer.current = window.setTimeout(() => {
      const el = tip.current;
      if (!el?.isConnected) return;
      el.showPopover?.();
      const r = target.getBoundingClientRect();
      const t = el.getBoundingClientRect();
      // Its side, else the opposite one, then slid to stay 8 px inside the window (02).
      const left = side !== 'right' ? r.left + (r.width - t.width) / 2 : r.right + 12 + t.width + 8 <= innerWidth ? r.right + 12 : r.left - 12 - t.width;
      const top = side === 'right' ? r.top + (r.height - t.height) / 2 : r.bottom + 4 + t.height + 8 <= innerHeight ? r.bottom + 4 : r.top - 4 - t.height;
      const inside = (at: number, size: number, room: number) => Math.max(8, Math.min(at, room - size - 8));
      el.style.left = `${inside(left, t.width, innerWidth)}px`;
      el.style.top = `${inside(top, t.height, innerHeight)}px`;
    }, TOOLTIP_DELAY);
  };
  const hide = () => {
    clearTimeout(timer.current);
    if (tip.current?.matches?.(':popover-open')) tip.current.hidePopover();
  };
  return {
    triggerProps: {
      onPointerEnter: (e: { currentTarget: HTMLElement; pointerType: string }) => e.pointerType === 'mouse' && show(e.currentTarget),
      onPointerLeave: hide,
      onFocus: (e: { currentTarget: HTMLElement }) => e.currentTarget.matches(':focus-visible') && show(e.currentTarget),
      onBlur: hide,
      onPointerDown: hide,
    },
    tooltip: (
      <div ref={tip} className="tooltip" popover="manual" role="tooltip">
        {label}
      </div>
    ),
  };
}

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** The accessible name and the tooltip. */
  label: string;
  icon: string;
  size?: number;
  ref?: React.Ref<HTMLButtonElement>;
  tooltip?: 'below' | 'right' | false;
  /** A tooltip that says more than the name (a shortcut). */
  hint?: string;
}

export function IconButton({ label, icon, size = 24, tooltip = 'below', hint, className, ...rest }: IconButtonProps) {
  const { triggerProps, tooltip: tip } = useTooltip(hint ?? label, tooltip || 'below');
  return (
    <>
      <button
        type="button"
        aria-label={label}
        className={`icon-btn${className ? ` ${className}` : ''}`}
        {...(tooltip ? triggerProps : {})}
        {...rest}
      >
        <Icon svg={icon} size={size} />
      </button>
      {tooltip && tip}
    </>
  );
}

/** "Copy", then "Copied" for 2 s; `name` adds what it copies to the accessible name. */
export function CopyButton({ text, label = t('common.copy'), name, small }: { text: string; label?: string; name?: string; small?: boolean }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <Button small={small} onClick={copy} aria-label={name}>
      {copied ? t('common.copied') : label}
    </Button>
  );
}
